<template>
  <div id="app">
    <app-layout>
      <template #status>
        <AppStatus
          :isWatching="isWatching"
          :status="status"
          :hasResponse="response !== null"
          :time="time"
        />
      </template>

      <template #loader>
        <LoadingIndicator :isLoading="status == 'locating'" />
      </template>

      <template #response>
        <div
          class="response margins-off"
          v-bind:class="{ error: status == 'error' }"
        >
          <div aria-live="assertive" role="region">
            <transition name="crossfade">
              <ErrorMessage v-if="status == 'error'" :error="error" />
            </transition>
            <location-item v-if="response" :address="response">
              <div class="pill">
                <icon-accuracy>Accuracy</icon-accuracy>
                {{ accuracyInMeters }}
              </div>
              <div class="location-controls large">
                <button
                  @click="
                    currentLocationIsSaved
                      ? deleteLocation(response)
                      : saveLocation(response)
                  "
                  class="button toggle-button"
                  :class="{ active: currentLocationIsSaved }"
                >
                  <span>
                    <icon-save>{{
                      currentLocationIsSaved ? "Saved" : "Save"
                    }}</icon-save>
                  </span>
                </button>
                <DropPinAnchor :location="response" />
              </div>
            </location-item>
          </div>
        </div>
      </template>

      <template #controls>
        <AppControls
          @refresh="geolocation"
          @watch="toggleWatch"
          @openLocationsModal="toggleModal('locations')"
          @openInformationModal="toggleModal('information')"
          :locationsCount="locations.length"
          :hasUpdated="status == 'located' && isWatching"
          :isLocating="status == 'locating'"
          :isWatching="isWatching"
        />
      </template>
    </app-layout>

    <modal-view
      heading="Saved Locations"
      :active="modalsActive.locations"
      @close="toggleModal('locations')"
    >
      <template #heading-controls>
        <button @click="deleteAllLocations" class="pill danger">Clear</button>
      </template>
      <LocationList
        :locations="locations"
        @delete="deleteLocationWithConfirmation"
      />
    </modal-view>

    <modal-view
      heading="About GeOh.me"
      :active="modalsActive.information"
      @close="toggleModal('information')"
    >
      <AboutGeoh />
    </modal-view>
  </div>
</template>

<script>
const localStorage = require("./api/localStorage");
const geolocationApi = require("./api/geolocationApi");

import "./App.scss";
import AppLayout from "@/components/AppLayout";
import AppStatus from "@/components/AppStatus";
import AppControls from "@/components/AppControls";
import LoadingIndicator from "@/components/LoadingIndicator";
import LocationItem from "@/components/LocationItem";
import LocationList from "@/components/LocationList";
import ModalView from "@/components/ModalView";
import ErrorMessage from "@/components/ErrorMessage";
import DropPinAnchor from "@/components/DropPinAnchor";
import AboutGeoh from "@/pages/AboutGeoh";
import IconSave from "@/components/icons/IconSave";
import IconAccuracy from "@/components/icons/IconAccuracy";

export default {
  name: "app",
  components: {
    AppLayout,
    AboutGeoh,
    AppStatus,
    LocationItem,
    LocationList,
    AppControls,
    LoadingIndicator,
    IconSave,
    IconAccuracy,
    DropPinAnchor,
    ErrorMessage,
    ModalView
  },

  data() {
    return {
      error: {},
      locations: [],
      position: Object,
      response: null,
      status: "locating",
      watchId: null,
      modalsActive: {
        locations: false,
        information: false
      }
    };
  },

  computed: {
    coords: function() {
      if (!this.position) return null;
      const { coords } = this.position;
      return coords.latitude + "," + coords.longitude;
    },

    accuracyInMeters: function() {
      if (!this.position) return null;
      return Math.round(this.position.coords.accuracy) + "m";
    },

    time: function() {
      if (!this.position) return null;
      return new Date(this.position.timestamp);
    },

    isWatching: function() {
      return this.watchId ? true : false;
    },

    currentLocationIsSaved: function() {
      const alreadySaved = this.locations.filter(
        loc => loc.label == this.response.label
      );
      return alreadySaved.length ? true : false;
    }
  },

  watch: {
    // Watch locations change for localStorage persistence
    locations: {
      handler: function(locations) {
        localStorage.save(locations);
      },
      deep: true
    },
    // Is this the best way to handle resetting the error object?
    status: function(val, oldVal) {
      if (oldVal == "error") this.error = {};
    }
  },

  created: function() {
    this.geolocation();
  },

  beforeMount: function() {
    this.locations = localStorage.fetch();
  },

  methods: {
    toggleModal: function(type) {
      this.modalsActive[type] = !this.modalsActive[type];
    },

    geolocation: function() {
      this.clearWatch();

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          this.geolocationSuccess,
          this.geolocationError,
          this.geolocationOptions
        );
      } else {
        this.noGeolocationSupport;
      }
    },

    geolocationSuccess: function(position) {
      this.status = "locating";
      // Cache the geolocation response
      this.position = position;

      const response = geolocationApi.reverseGeocode(position);

      response
        .then(data => {
          const { response } = data;

          this.response =
            response.view.length > 0
              ? response.view[0].result[0].location.address
              : null;
          this.response.timestamp = this.position.timestamp;
          this.response.coords = this.coords;
          this.status = "located";
        })
        .catch(() => {
          this.status = "error";
          this.error.emoji = "☠️";
          this.error.message = "Looks like you are in No Man’s Land.";
        });
    },

    geolocationError: function(error) {
      const err = geolocationApi.error(error);
      this.status = "error";
      this.error = err;
    },

    geolocationOptions: function() {
      return geolocationApi.options();
    },

    toggleWatch: function() {
      if (this.watchId) this.clearWatch();
      else this.watchPosition();
      return true;
    },

    watchPosition: function() {
      if ("geolocation" in navigator) {
        this.watchId = navigator.geolocation.watchPosition(
          this.geolocationSuccess,
          this.geolocationError,
          this.geolocationOptions
        );
      } else {
        this.noGeolocationSupport;
      }
    },

    clearWatch: function() {
      this.status = "located";
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    },

    saveLocation: function(response) {
      this.locations.unshift(response);
    },

    deleteLocation: function(location) {
      for (let i = 0; i < this.locations.length; i++) {
        const loc = this.locations[i];

        if (loc.label == location.label) {
          this.locations.splice(this.locations.indexOf(loc), 1);
          stop;
          break;
        }
      }
    },

    deleteLocationWithConfirmation: function(location) {
      if (confirm("Delete this location? This cannot be undone.")) {
        this.deleteLocation(location);
      }
    },

    deleteAllLocations: function() {
      if (
        confirm(
          "You are you sure you want to delete all your saved locations? This cannot be undone."
        )
      ) {
        this.locations = [];
      }
    },

    noGeolocationSupport: function() {
      this.status = "error";
      this.error.emoji = "😵";
      this.error.message =
        "Sorry! It looks like your device or browser does not support geolocation.";
    }
  }
};
</script>

<style lang="scss">
#app {
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  @media screen and (orientation: landscape) {
    overflow-x: hidden;
    overflow-y: scroll;
    position: relative;
    -webkit-overflow-scrolling: touch;
  }
}

.response {
  position: relative;
}

.button {
  background-color: $color-purple;
  border: 2px solid $color-purple-highlight;
  border-radius: 50%;
  display: inline-block;
  font-size: 1em;
  overflow: hidden;
  transition: border-color 0.2s ease;
  vertical-align: middle;

  &:focus,
  &:active {
    border-color: $color-accent;
    outline: 0;
  }

  + .button {
    margin-left: 0.5em;
  }
}

.button > span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2em;
  height: 2em;
  outline: none;
}

.toggle-button {
  .icon {
    fill: none;
    stroke: red;
    stroke-width: 2;
  }

  &.active .icon {
    fill: red;
    animation: heartbeat 0.25s ease;
  }
}

.pill {
  background-color: $color-purple-highlight;
  border-radius: 20px;
  display: inline-block;
  font-size: 0.75em;
  font-weight: 700;
  line-height: 1;
  padding: 0.25em 0.5em;

  &.danger {
    background-color: red;
  }
}

svg {
  outline: none;
}
</style>
